import React, { useMemo, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { AVAILABILITY, LIST_VISIBILITY, ONLINE_PURCHASE } from '~/consts/schedule';

import { formatDateSlashesWithClock, isBetween } from '~/services/TimeService';

import { CampaignScheduleDropdownRow } from './CampaignScheduleExpandableRow';
import { buildCampaignScheduleBrandRows } from './campaignScheduleBrandRows';
import { getScheduleTypeLabel } from './campaignScheduleHistoryDisplay';
import { BRAND_ROWS_TABLE_COLUMN_COUNT, handleExpandRowKeyDown, toggleExpandedRow } from './campaignScheduleTabLayout';
import type { BrandSchedule, CampaignScheduleDateEntry } from './types';

// lux_plus is shown bare (its row's Type chip already identifies it); every other type present gets
// a labeled, tooltipped pill above its date range.
const PILL_CONFIG: Partial<
  Record<BrandSchedule['type'], { color: 'warning' | 'default' | 'primary'; tooltip: string }>
> = {
  [AVAILABILITY]: { color: 'default', tooltip: 'Staff-only purchasing; BNBL date cutoff' },
  [ONLINE_PURCHASE]: { color: 'warning', tooltip: 'Purchasable via direct link (not in search)' },
  [LIST_VISIBILITY]: { color: 'primary', tooltip: 'Visible in search, carousels, and homepage' },
};

function isRowActive(dateEntries: Array<CampaignScheduleDateEntry>) {
  const now = new Date();
  return dateEntries.some((entry) => isBetween(entry.start, entry.end, now, undefined, '[]'));
}

interface Props {
  emptyMessage: string;
  schedules: Array<BrandSchedule>;
}

export default function CampaignScheduleBrandRowsTable({ emptyMessage, schedules }: Props) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const brandRows = useMemo(() => buildCampaignScheduleBrandRows(schedules), [schedules]);

  if (brandRows.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 1.5, textAlign: 'center' }} variant="body2">
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 40 }}>#</TableCell>
          <TableCell>Schedule</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Dates</TableCell>
          <TableCell>Regions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {brandRows.map((row, index) => {
          const isExpanded = expandedRows.has(row.key);
          const isActive = isRowActive(row.dateEntries);

          return (
            <React.Fragment key={row.key}>
              <TableRow>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Typography fontWeight={700} variant="body2">
                    {row.brand}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    color={row.isLuxPlus ? undefined : 'info'}
                    label={row.isLuxPlus ? 'LuxPlus' : 'Normal'}
                    size="small"
                    sx={(theme) => ({
                      bgcolor: alpha(row.isLuxPlus ? theme.palette.luxPlus.main : theme.palette.info.main, 0.12),
                      color: row.isLuxPlus ? theme.palette.luxPlus.main : undefined,
                      borderColor: row.isLuxPlus ? theme.palette.luxPlus.main : undefined,
                      fontWeight: 600,
                    })}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    color={isActive ? 'success' : 'default'}
                    label={isActive ? 'Active' : 'Inactive'}
                    size="small"
                    sx={{
                      bgcolor: (theme) => alpha(isActive ? theme.palette.success.main : theme.palette.grey[500], 0.12),
                      fontWeight: 600,
                    }}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Stack spacing={1}>
                    {row.dateEntries.map((entry) => {
                      const pillConfig = PILL_CONFIG[entry.type];

                      return (
                        <Stack key={entry.type} spacing={0.5}>
                          {pillConfig && (
                            <Tooltip arrow title={pillConfig.tooltip}>
                              <Chip
                                color={pillConfig.color}
                                label={getScheduleTypeLabel(entry.type)}
                                size="small"
                                sx={{ alignSelf: 'flex-start' }}
                                variant="outlined"
                              />
                            </Tooltip>
                          )}
                          <Typography variant="body2">
                            {formatDateSlashesWithClock(entry.start)} → {formatDateSlashesWithClock(entry.end)}
                          </Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack
                    alignItems="center"
                    direction="row"
                    role="button"
                    spacing={0.5}
                    sx={{ cursor: 'pointer' }}
                    tabIndex={0}
                    onClick={() => setExpandedRows((current) => toggleExpandedRow(current, row.key))}
                    onKeyDown={(event) =>
                      handleExpandRowKeyDown(event, () =>
                        setExpandedRows((current) => toggleExpandedRow(current, row.key)),
                      )
                    }
                  >
                    <Typography variant="body2">
                      {row.regions.length === 1 ? row.regions[0] : `${row.regions.length} regions`}
                    </Typography>
                    {isExpanded ? (
                      <KeyboardArrowDownIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowRightIcon fontSize="small" />
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
              <CampaignScheduleDropdownRow colSpan={BRAND_ROWS_TABLE_COLUMN_COUNT} isExpanded={isExpanded}>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {row.regions.map((region) => (
                    <Chip key={region} label={region} size="small" variant="outlined" />
                  ))}
                </Stack>
              </CampaignScheduleDropdownRow>
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
